import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup, useMapContext,
} from 'react-simple-maps';
import ReactCountryFlag from 'react-country-flag';
import { FaAndroid, FaApple } from 'react-icons/fa6';
import {
  BatteryFull, BatteryLow, BatteryMedium, BatteryWarning, Check, ChevronDown, Globe, MapPin, Smartphone, Zap,
} from 'lucide-react';

import { Pill, WorkspaceTabs } from './parts';
import { tx } from './i18n';
import { AppLogo } from './AppPhone';
import { countryCodeForGeography, countryName } from './countries';
import { GEO_URL } from './routeMap';
import './mobile.css';

export const NUMBER = new Intl.NumberFormat();
export const DECIMAL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
export const COMPACT = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

// The section's charts are navy, darkest for what matters most. Colour is
// kept for meaning: a status, or a platform beside its logo.
export const CHART = {
  ink: '#203a85',
  deep: '#0a1e57',
  mid: '#7185bd',
  soft: '#a9b5d9',
  pale: '#e6e9f2',
  grid: '#eef1f7',
  tick: { fill: '#8b93ad', fontSize: 11 },
  tooltip: { borderRadius: 8, fontSize: 12, border: '1px solid rgba(32,58,133,.12)' },
  // What the pointer lights up: a soft column over bars, a dashed rule over lines.
  cursor: { fill: 'rgba(32,58,133,.05)' },
  lineCursor: { stroke: 'rgba(32,58,133,.28)', strokeWidth: 1, strokeDasharray: '3 3' },
  failed: '#d9957f',
};

// A chart's hover card: when the point is, each series with its mark and value,
// and under a hairline a few facts that say what those numbers mean. Given to
// recharts as <Tooltip content={<ChartTip ... />} />, which passes it the point.
// `rows(point)` returns { name, color, mark: 'bar' | 'line' | 'band', text, share };
// a row whose text is null is left out. `facts(point)` returns { label, value, tone }.
export function ChartTip({ active, payload, label, title, rows, facts }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const point = payload[0].payload || {};
  const heading = title ? title(label, point) : { main: String(label) };
  const shown = (rows ? rows(point) : payload.map((entry) => ({
    name: entry.name, color: entry.color, mark: 'bar',
    text: entry.value === null || entry.value === undefined ? null : NUMBER.format(entry.value),
  }))).filter((row) => row && row.text !== null && row.text !== undefined);
  const extra = facts ? facts(point).filter((fact) => fact && (fact.note || (fact.value !== null && fact.value !== undefined))) : [];
  return (
    <div className="xo-mob-tip">
      <div className="xo-mob-tip-head">
        <span className="xo-mob-tip-title">{heading.main}</span>
        {heading.sub && <span className="xo-mob-tip-sub">{heading.sub}</span>}
      </div>
      {shown.length > 0 && (
        <div className="xo-mob-tip-rows">
          {shown.map((row) => (
            <div key={row.name} className="xo-mob-tip-row">
              <i className={`xo-mob-tip-mark is-${row.mark || 'bar'}`} style={{ background: row.color }} />
              <span className="xo-mob-tip-name">{row.name}</span>
              <span className="xo-mob-tip-value">{row.text}{row.share && <span className="xo-mob-tip-share">{row.share}</span>}</span>
            </div>
          ))}
        </div>
      )}
      {extra.length > 0 && (
        <div className="xo-mob-tip-facts">
          {extra.map((fact) => (fact.note
            ? <div key={fact.note} className="xo-mob-tip-fact is-note"><span>{fact.note}</span></div>
            : (
              <div key={fact.label} className="xo-mob-tip-fact">
                <span>{fact.label}</span>
                <span className={`xo-mob-tip-fact-value${fact.tone ? ` is-${fact.tone}` : ''}`}>{fact.value}</span>
              </div>
            )))}
        </div>
      )}
    </div>
  );
}

// A date or time on someone's own clock, or on the viewer's when that zone is unknown.
function onClock(at, options, zone) {
  try {
    return new Date(at).toLocaleString(undefined, { ...options, timeZone: zone || undefined });
  } catch {
    return new Date(at).toLocaleString(undefined, options);
  }
}

export function tipClock(at, zone) {
  return onClock(at, { hour: '2-digit', minute: '2-digit' }, zone);
}

// A day as a hover card names it, with its weekday, on the given clock.
export function tipDay(at, zone) {
  return { main: onClock(at, { weekday: 'short', day: 'numeric', month: 'short' }, zone) };
}

// An hour as a hover card names it: its span, and the day underneath.
export function tipHour(at, zone) {
  return { main: tx('ui.mobile.tip_hour_span', { from: tipClock(at, zone), to: tipClock(at + HOUR, zone) }), sub: tipDay(at, zone).main };
}

// The hover title for a page's points: an hour when they are an hour apart, a day otherwise.
export function tipPoint(step) {
  return (at) => (hourly(step) ? tipHour(at) : tipDay(at));
}

// How a value compares with the range's average, as a fact: "+23%" or "−12%".
export function versusAverage(value, average) {
  if (value === null || value === undefined || !average) return null;
  const change = ((value - average) / average) * 100;
  if (Math.abs(change) < 0.5) return { label: tx('ui.mobile.tip_vs_average'), value: tx('ui.mobile.tip_at_average') };
  const rounded = Math.abs(change) >= 10 ? Math.round(change) : Math.round(change * 10) / 10;
  return {
    label: tx('ui.mobile.tip_vs_average'),
    value: `${rounded > 0 ? '+' : '−'}${NUMBER.format(Math.abs(rounded))}%`,
    tone: rounded > 0 ? 'up' : 'down',
  };
}

// Hover rows for a stacked chart: each part with its value and share of the point's
// whole, in the given order or largest first. Parts with nothing are left out.
export function stackRows(point, parts, { sorted = false, format = (value) => NUMBER.format(value) } = {}) {
  const total = parts.reduce((sum, part) => sum + (Number(point[part.key]) || 0), 0);
  const rows = parts
    .filter((part) => Number(point[part.key]) > 0)
    .map((part) => ({ name: part.name, color: part.color, mark: 'bar', value: Number(point[part.key]),
      text: format(Number(point[part.key])), share: parts.length > 1 ? percent(Number(point[part.key]), total) : null }));
  return sorted ? rows.sort((a, b) => b.value - a.value) : rows;
}

// The average of the values that are there.
export function averageOf(values) {
  const present = values.filter((value) => value !== null && value !== undefined && Number.isFinite(value));
  return present.length === 0 ? null : present.reduce((sum, value) => sum + value, 0) / present.length;
}

// A counted phrase keeps its words for one under key_one, like the rest of the portal.
export function counted(key, count, variables = {}, format = NUMBER) {
  return tx(count === 1 ? `${key}_one` : key, { ...variables, count: format.format(count) });
}

// The app version a session or account was on: a number from 5.4, and one
// "earlier version" for every build before it.
export function versionLabel(version) {
  if (!version) return null;
  return version === 'earlier' ? tx('ui.mobile.version_earlier') : tx('ui.mobile.version_value', { version });
}

const MINUTE = 60000;
const HOUR = 3600000;
const DAY = 86400000;

export function ago(at, now = Date.now()) {
  if (at === null || at === undefined) return tx('ui.mobile.never');
  const delta = at - now;
  const size = Math.abs(delta);
  if (size < MINUTE) return tx('ui.mobile.just_now');
  if (size < HOUR) return RELATIVE.format(Math.round(delta / MINUTE), 'minute');
  if (size < DAY) return RELATIVE.format(Math.round(delta / HOUR), 'hour');
  if (size < 30 * DAY) return RELATIVE.format(Math.round(delta / DAY), 'day');
  return new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function when(at) {
  if (at === null || at === undefined) return '—';
  return new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function day(at) {
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Whether a page's chart points are an hour apart, as they are when the range
// is a day. `step` is what the page's answer says.
export function hourly(step) {
  return step === HOUR;
}

// A chart point's label: its hour when points are an hour apart, its day otherwise.
export function pointLabel(step) {
  if (!hourly(step)) return day;
  return (at) => new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function span(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} m ${String(Math.round(seconds % 60)).padStart(2, '0')} s`;
}

const units = new Map();
function unit(name) {
  if (!units.has(name)) units.set(name, new Intl.NumberFormat(undefined, { style: 'unit', unit: name, unitDisplay: 'short', maximumFractionDigits: 0 }));
  return units.get(name);
}

// How long something lasted, in the viewer's own units: 40 sec, 6 min, 1 hr 12 min.
export function lengthOf(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return unit('second').format(seconds);
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return unit('minute').format(minutes);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? unit('hour').format(hours) : `${unit('hour').format(hours)} ${unit('minute').format(rest)}`;
}

export function statusTone(status) {
  switch (status) {
    case 'active': return 'ok';
    case 'invited': return 'info';
    case 'pending': return 'warn';
    case 'disabled': return 'bad';
    default: return 'flat';
  }
}

// Two letters from a name, one from an address when there is no name.
export function initials(name, email) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length > 0) return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
  const letter = String(email || '').match(/[a-z]/i);
  return letter ? letter[0].toUpperCase() : '?';
}

export function percent(part, whole) {
  if (!whole) return '—';
  const value = (part / whole) * 100;
  return `${value >= 10 || value === 0 ? value.toFixed(0) : value.toFixed(1)}%`;
}

// The wall clock where someone is, from the timezone their requests came from.
export function localClock(timeZone, at = Date.now()) {
  if (!timeZone) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone, hour: '2-digit', minute: '2-digit', weekday: 'short' }).format(at);
  } catch {
    return null;
  }
}

export function useNow(everyMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);
  return now;
}

// The last day, week, month, six months or year.
export function RangeTabs({ value, onChange }) {
  const tabs = [
    { value: 1, label: tx('ui.mobile.range_day') },
    { value: 7, label: tx('ui.analytics.7_days_0a8dd6ad') },
    { value: 30, label: tx('ui.analytics.30_days_7d4278a8') },
    { value: 180, label: tx('ui.mobile.range_six_months') },
    { value: 365, label: tx('ui.mobile.range_year') },
  ];
  return <WorkspaceTabs value={value} tabs={tabs} onChange={onChange} label={tx('ui.mobile.time_range')} className="is-compact xo-mob-range" />;
}

// A filter as the portal's lists have them: a button naming what is picked, and
// a menu of the choices with their counts. The first option is "any".
export function FilterMenu({ icon: Icon, label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (box.current && !box.current.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('keydown', escape); };
  }, [open]);
  const picked = options.find((option) => option.value === value) || options[0];
  const count = (option) => (option.count === undefined || option.count === null ? <i /> : <b>{NUMBER.format(option.count)}</b>);
  return (
    <div className="xo-product-filter-control xo-mob-filter" ref={box}>
      <button type="button" className={`xo-product-filter-button${picked.value !== options[0].value ? ' is-filtered' : ''}`}
        aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon aria-hidden="true" /><span>{picked.label}</span>{count(picked)}<ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-product-filter-menu" role="menu" aria-label={label}>
          {options.map((option) => (
            <button type="button" role="menuitemradio" aria-checked={option.value === picked.value} key={option.value}
              className={option.value === picked.value ? 'is-active' : ''} onClick={() => { onChange(option.value); setOpen(false); }}>
              <span>{option.label}</span>{count(option)}{option.value === picked.value ? <Check aria-hidden="true" /> : <i />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Where a person is, as the account list shows it: the country and city of
// their latest app request. "unknown" gathers everyone without a country.
export const UNKNOWN_PLACE = 'unknown';

export function placeMatches(place, country, city) {
  if (country === UNKNOWN_PLACE && place.country) return false;
  if (country && country !== UNKNOWN_PLACE && place.country !== country) return false;
  return !city || place.city === city;
}

// Country and city menus over a list of places ({ country, city }), each
// choice with its count; the cities follow the chosen country.
export function PlaceFilters({ places, country, city, onChange }) {
  const countries = new Map();
  let unplaced = 0;
  places.forEach((place) => {
    if (place.country) countries.set(place.country, (countries.get(place.country) || 0) + 1);
    else unplaced += 1;
  });
  const local = places.filter((place) => placeMatches(place, country, ''));
  const cities = new Map();
  local.forEach((place) => {
    if (!place.city) return;
    const entry = cities.get(place.city) || { count: 0, country: place.country };
    cities.set(place.city, { ...entry, count: entry.count + 1 });
  });
  const countryOptions = [
    { value: '', label: tx('ui.mobile.any_country'), count: places.length },
    ...[...countries].sort((a, b) => b[1] - a[1]).map(([code, count]) => ({ value: code, label: countryName(code), count })),
    ...(unplaced > 0 ? [{ value: UNKNOWN_PLACE, label: tx('ui.mobile.location_unknown'), count: unplaced }] : []),
  ];
  const cityOptions = [
    { value: '', label: tx('ui.mobile.any_city'), count: local.length },
    ...[...cities].sort((a, b) => b[1].count - a[1].count).map(([name, entry]) => ({
      value: name, label: country || !entry.country ? name : `${name}, ${countryName(entry.country)}`, count: entry.count,
    })),
  ];
  return (
    <>
      <FilterMenu icon={Globe} label={tx('ui.mobile.country')} value={country} options={countryOptions}
        onChange={(value) => onChange({ country: value, city: '' })} />
      <FilterMenu icon={MapPin} label={tx('ui.mobile.city')} value={city} options={cityOptions}
        onChange={(value) => onChange({ country, city: value })} />
    </>
  );
}

export function PlatformMark({ platform, withLabel = false }) {
  const key = String(platform || '').toLowerCase();
  const label = key === 'ios' ? 'iOS' : key === 'android' ? 'Android' : tx('ui.mobile.unknown_platform');
  const Icon = key === 'ios' ? FaApple : key === 'android' ? FaAndroid : Smartphone;
  return (
    <span className={`xo-mob-platform is-${key === 'ios' || key === 'android' ? key : 'other'}`} title={label}>
      <Icon aria-hidden="true" />{withLabel && <span>{label}</span>}
    </span>
  );
}

export function Flag({ code, withName = false }) {
  if (!code || !/^[A-Z]{2}$/i.test(code)) return <span className="xo-mob-flag is-empty">—</span>;
  const upper = code.toUpperCase();
  return (
    <span className="xo-mob-flag" title={countryName(upper)}>
      <ReactCountryFlag countryCode={upper} svg aria-label={countryName(upper)} />
      {withName && <span>{countryName(upper)}</span>}
    </span>
  );
}

export function originLabel(origin) {
  switch (origin) {
    case 'r01': return tx('ui.mobile.origin_r01');
    case 'b01': return tx('ui.mobile.origin_b01');
    case 'apple': return tx('ui.mobile.origin_apple');
    case 'android': return tx('ui.mobile.origin_android');
    case 'manual': return tx('ui.mobile.origin_manual');
    default: return origin || tx('ui.mobile.origin_unknown');
  }
}

export function BatteryMark({ value, charging }) {
  if (value === null || value === undefined) return <span className="xo-mob-battery is-unknown">—</span>;
  const Icon = value <= 10 ? BatteryWarning : value <= 30 ? BatteryLow : value <= 70 ? BatteryMedium : BatteryFull;
  const tone = value <= 15 ? 'bad' : value <= 35 ? 'warn' : 'ok';
  return (
    <span className={`xo-mob-battery t-${tone}${charging ? ' is-charging' : ''}`} title={tx('ui.mobile.battery_value', { value })}>
      <Icon aria-hidden="true" /><strong>{value}%</strong>{charging && <Zap aria-hidden="true" className="xo-mob-charging" />}
    </span>
  );
}

const METRICS = ['heart_rate', 'resting_heart_rate', 'hrv', 'steps', 'distance', 'calories', 'sleep', 'exercise',
  'blood_pressure', 'respiratory_rate', 'blood_oxygen', 'body_temperature', 'glucose', 'water'];
const PUSH_TYPES = ['sync_request', 'no_habits_today', 'low_battery', 'test_notification', 'non_sync'];
const EMAIL_REASONS = ['email_verification', 'password_reset', 'login_otp', 'email_change', 'account_deletion', 'test', 'collection_activation'];

export function pushTypeLabel(type) {
  return PUSH_TYPES.includes(type) ? tx(`ui.mobile.push_type_${type}`) : type;
}
export function emailReasonLabel(reason) {
  return EMAIL_REASONS.includes(reason) ? tx(`ui.mobile.email_reason_${reason}`) : reason;
}
export function metricLabel(metric) {
  return METRICS.includes(metric) ? tx(`ui.mobile.metric_${metric}`) : metric;
}

// A push as it looked on the phone: the app's icon and name, the title and the
// text. Admin+ also see a push that never arrived, marked as such.
export function PushCard({ push, now, to = null }) {
  const known = push.outcome !== undefined;
  const delivered = !known || push.outcome === 'sent';
  const body = (
    <>
      <span className="xo-mob-push-icon"><AppLogo color="#fff" height={13} /></span>
      <span className="xo-mob-push-text">
        <span className="xo-mob-push-head"><b>{tx('ui.mobile.app_name')}</b><small>{ago(push.at, now)}</small></span>
        {push.silent ? <em>{tx('ui.mobile.silent_push')}</em> : <><strong>{push.title || pushTypeLabel(push.type)}</strong>{push.body && <span>{push.body}</span>}</>}
        <span className="xo-mob-push-meta">
          <PlatformMark platform={push.platform} />
          <span>{pushTypeLabel(push.type)}</span>
          {push.language && <span>{String(push.language).toUpperCase()}</span>}
          {known && !delivered && <Pill tone="flat">{tx(`ui.mobile.outcome_${push.outcome}`)}</Pill>}
          {push.latencyMs !== undefined && push.latencyMs !== null && <span>{span(push.latencyMs)}</span>}
        </span>
      </span>
    </>
  );
  const className = `xo-mob-push${delivered ? '' : ' is-undelivered'}`;
  return to ? <Link className={className} to={to}>{body}</Link> : <div className={className}>{body}</div>;
}

// The headline numbers of a screen, one flat strip, the most important first.
export function KpiRow({ items }) {
  return (
    <div className="xo-mob-kpis" style={{ '--xo-mob-kpi-count': items.length }}>
      {items.map((item) => (
        <div key={item.label} className="xo-mob-kpi">
          <span className="xo-mob-kpi-label">{item.label}</span>
          <strong className="xo-mob-kpi-value">{item.value}</strong>
          {item.detail && <span className="xo-mob-kpi-detail">{item.detail}</span>}
          {item.trend && <Sparkline values={item.trend} />}
        </div>
      ))}
    </div>
  );
}

// A white panel with a heading, what it shows underneath, and room for a control.
export function Panel({ title, detail, aside = null, children, className = '', flush = false }) {
  return (
    <section className={`xo-mob-panel${flush ? ' is-flush' : ''} ${className}`}>
      {(title || aside) && (
        <header className="xo-mob-panel-head">
          <div>{title && <h2>{title}</h2>}{detail && <p>{detail}</p>}</div>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

// A few numbers for a panel's head, each with its word under it.
export function HeadFacts({ items }) {
  return (
    <dl className="xo-mob-head-facts">
      {items.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
    </dl>
  );
}

export function Sparkline({ values, width = 112, height = 28 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const points = values.map((value, index) => `${(index * step).toFixed(1)},${(height - 2 - (value / max) * (height - 4)).toFixed(1)}`);
  return (
    <svg className="xo-mob-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={`0,${height} ${points.join(' ')} ${width},${height}`} className="xo-mob-spark-fill" />
      <polyline points={points.join(' ')} className="xo-mob-spark-line" />
    </svg>
  );
}

// A share of a whole as a thin navy bar.
export function Share({ value, total }) {
  const width = total > 0 ? Math.max(value > 0 ? 2 : 0, (value / total) * 100) : 0;
  return <span className="xo-mob-share" aria-hidden="true"><i style={{ width: `${width}%` }} /></span>;
}

const WEEKDAY_BASE = Date.UTC(2024, 0, 1); // A Monday.
function weekdayName(index) {
  return new Date(WEEKDAY_BASE + index * DAY).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
}

// Seven rows of twenty-four local hours, darker where more happened.
export function HourHeatmap({ grid, countKey = 'ui.mobile.requests_count', compact = false }) {
  const max = Math.max(1, ...grid.flat());
  const [hovered, setHovered] = useState(null);
  return (
    <div className={`xo-mob-heatmap${compact ? ' is-compact' : ''}`}>
      <div className="xo-mob-heat-grid" role="grid" aria-label={tx('ui.mobile.heatmap_label')}>
        {grid.map((row, weekday) => (
          <div key={weekday} className="xo-mob-heat-row" role="row">
            <span className="xo-mob-heat-day">{weekdayName(weekday)}</span>
            {row.map((value, hour) => {
              const level = value === 0 ? 0 : 0.12 + (Math.sqrt(value / max) * 0.88);
              return (
                <span key={hour} role="gridcell" className="xo-mob-heat-cell"
                  style={{ '--level': level.toFixed(3) }}
                  onMouseEnter={() => setHovered({ weekday, hour, value })} onMouseLeave={() => setHovered(null)}
                  aria-label={tx('ui.mobile.heat_cell', { day: weekdayName(weekday), hour: String(hour).padStart(2, '0'), value })} />
              );
            })}
          </div>
        ))}
        {!compact && (
          <div className="xo-mob-heat-hours" aria-hidden="true">
            <span />
            {Array.from({ length: 24 }, (_, hour) => <span key={hour}>{hour % 3 === 0 ? String(hour).padStart(2, '0') : ''}</span>)}
          </div>
        )}
      </div>
      <p className="xo-mob-heat-caption">
        {hovered
          ? tx('ui.mobile.heat_hover', { day: weekdayName(hovered.weekday), hour: String(hovered.hour).padStart(2, '0'), value: counted(countKey, hovered.value) })
          : tx('ui.mobile.heat_caption')}
      </p>
    </div>
  );
}

const FRAME_MAX_ZOOM = 8;

// Frames the points given: the whole world when they are far apart, closer in
// the nearer together they are, as close as the map's detail allows for one
// spot. Scrolling the page over it never zooms it.
function Framed({ points, children }) {
  const { width, height, projection } = useMapContext();
  const view = useMemo(() => {
    const projected = points.map((point) => projection(point)).filter((xy) => xy && Number.isFinite(xy[0]) && Number.isFinite(xy[1]));
    if (projected.length === 0) return { center: projection.invert([width / 2, height / 2]), zoom: 1 };
    const xs = projected.map((xy) => xy[0]);
    const ys = projected.map((xy) => xy[1]);
    const [left, right, top, bottom] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const zoom = Math.max(1, Math.min(FRAME_MAX_ZOOM, (width * 0.5) / Math.max(right - left, 1), (height * 0.5) / Math.max(bottom - top, 1)));
    if (zoom < 1.2) return { center: projection.invert([width / 2, height / 2]), zoom: 1 };
    return { center: projection.invert([(left + right) / 2, (top + bottom) / 2]), zoom };
  }, [points, projection, width, height]);
  return (
    <ZoomableGroup center={view.center} zoom={view.zoom} minZoom={1} maxZoom={FRAME_MAX_ZOOM} filterZoomEvent={() => false}>
      {children(view.zoom)}
    </ZoomableGroup>
  );
}

// The world with where the app is used shaded in navy, and a dot wherever a
// request arrived in the last hours, the freshest ones pulsing. With `fit` the
// map frames the dots instead of showing the whole world. A ping stands for one
// arrival unless it carries a `count`; with `pingCountKey` a dot's hover says how
// many, and a dot whose time is unknown (`at` 0) never pulses or says when.
export function PulseMap({
  countries = [], pings = [], valueKey = 'accounts', valueLabel = null, pingCountKey = null, label = null, height = 340, fit = false,
}) {
  const [hovered, setHovered] = useState(null);
  const byCode = useMemo(() => new Map(countries.map((country) => [country.code, country])), [countries]);
  const max = Math.max(1, ...countries.map((country) => Number(country[valueKey] || 0)));
  const now = useNow(20000);
  const places = useMemo(() => {
    const merged = new Map();
    for (const ping of pings) {
      const key = `${ping.city}|${ping.country}`;
      if (!merged.has(key)) merged.set(key, { ...ping, count: 0, latest: ping.at });
      const entry = merged.get(key);
      entry.count += typeof ping.count === 'number' ? ping.count : 1;
      entry.latest = Math.max(entry.latest, ping.at);
    }
    return [...merged.values()];
  }, [pings]);
  const points = useMemo(() => places.map((place) => [place.lng, place.lat]), [places]);
  const layers = (zoom) => (
    <>
      <Geographies geography={GEO_URL}>
        {({ geographies }) => geographies.map((geography) => {
          const code = countryCodeForGeography(geography);
          const country = code ? byCode.get(code) : null;
          const value = country ? Number(country[valueKey] || 0) : 0;
          // One person's map only marks the countries they were in, lightly, under their dots.
          const shade = fit ? 0.2 : 0.18 + Math.sqrt(value / max) * 0.62;
          const fill = value > 0 ? `rgba(32,58,133,${shade.toFixed(3)})` : '#e8ecf4';
          return (
            <Geography key={geography.rsmKey} geography={geography} vectorEffect="non-scaling-stroke"
              onMouseEnter={() => country && setHovered({ kind: 'country', code, ...country })}
              onMouseLeave={() => setHovered(null)}
              style={{
                default: { fill, stroke: '#fff', strokeWidth: 0.5, outline: 'none' },
                hover: { fill: country ? '#0a1e57' : fill, stroke: '#fff', strokeWidth: 0.6, outline: 'none' },
                pressed: { fill, outline: 'none' },
              }} />
          );
        })}
      </Geographies>
      {places.map((place) => {
        const fresh = place.latest > 0 && now - place.latest < 15 * MINUTE;
        // The same size on screen however close the map is framed.
        const radius = (2.4 + Math.min(3.6, Math.log2(place.count + 1))) / zoom;
        return (
          <Marker key={`${place.city}|${place.country}`} coordinates={[place.lng, place.lat]}
            onMouseEnter={() => setHovered({ kind: 'place', ...place })} onMouseLeave={() => setHovered(null)}>
            {fresh && <circle r={radius} className="xo-mob-ping-wave" vectorEffect="non-scaling-stroke" />}
            <circle r={radius} className={`xo-mob-ping${fresh ? ' is-fresh' : ''}`} vectorEffect="non-scaling-stroke" />
          </Marker>
        );
      })}
    </>
  );
  return (
    <div className="xo-mob-map" style={{ '--map-height': `${height}px` }}>
      <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 150 }} width={760} height={380}
        role="img" aria-label={label || tx('ui.mobile.map_label')}>
        {fit ? <Framed points={points}>{layers}</Framed> : layers(1)}
      </ComposableMap>
      {hovered && (
        <div className="xo-mob-map-focus" aria-live="polite">
          {hovered.kind === 'country' ? (
            <>
              <span><Flag code={hovered.code} /><strong>{countryName(hovered.code)}</strong></span>
              {!fit && <span><strong>{NUMBER.format(hovered[valueKey] || 0)}</strong><small>{valueLabel || tx('ui.mobile.people')}</small></span>}
            </>
          ) : (
            <>
              <span><Flag code={hovered.country} /><strong>{hovered.city || countryName(hovered.country)}</strong></span>
              {pingCountKey && <span><strong>{counted(pingCountKey, hovered.count)}</strong></span>}
              {hovered.latest > 0 && <span><strong>{ago(hovered.latest, now)}</strong><small>{tx('ui.mobile.latest')}</small></span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
